import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Beer,
  Zap,
  Coffee,
  Factory,
  Globe,
  Warehouse,
  ShoppingCart,
  Send,
  Lock,
  BadgeCheck,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import {
  PRODUCT_OPTIONS,
  ACTIVITY_OPTIONS,
  REGIME_OPTIONS,
  activityNeedsRegime,
  resolveLicense,
} from "../../lib/site-content";
import { Reveal } from "./Reveal";
import { scrollToId } from "../../lib/scroll";

const ICONS = {
  Beer,
  Zap,
  Coffee,
  Factory,
  Globe,
  Warehouse,
  ShoppingCart,
  Send,
  Lock,
  BadgeCheck,
};

const cardCls = (on) =>
  `flex items-center gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ${
    on
      ? "border-[#F59E0B] bg-[#F59E0B]/5"
      : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
  }`;

const iconCls = (on) =>
  `grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
    on ? "bg-[#F59E0B] text-[#0F172A]" : "bg-slate-100 text-[#0F172A]"
  }`;

export const LicentieChecker = () => {
  const [stage, setStage] = useState("product"); // product | activity | regime | result
  const [product, setProduct] = useState(null);
  const [activity, setActivity] = useState(null);
  const [regime, setRegime] = useState(null);

  const needsRegime = activityNeedsRegime(product, activity);
  const result =
    stage === "result" ? resolveLicense(product, activity, regime) : null;

  const productLabel = PRODUCT_OPTIONS.find((p) => p.value === product)?.label;
  const activityLabel = ACTIVITY_OPTIONS.find((a) => a.value === activity)?.label;
  const regimeLabel = REGIME_OPTIONS.find((r) => r.value === regime)?.label;

  const reset = () => {
    setProduct(null);
    setActivity(null);
    setRegime(null);
    setStage("product");
  };

  const pickProduct = (value) => {
    setProduct(value);
    setActivity(null);
    setRegime(null);
    setTimeout(() => setStage("activity"), 220);
  };

  const pickActivity = (value) => {
    setActivity(value);
    setRegime(null);
    const next = activityNeedsRegime(product, value) ? "regime" : "result";
    setTimeout(() => setStage(next), 220);
  };

  const pickRegime = (value) => {
    setRegime(value);
    setTimeout(() => setStage("result"), 220);
  };

  // Progress: product(1) -> activity(2) -> [regime(3)] -> result(last)
  const totalSteps = needsRegime ? 4 : 3;
  const stepNum = { product: 1, activity: 2, regime: 3, result: totalSteps }[stage];
  const progress = (stepNum / totalSteps) * 100;

  return (
    <section
      id="checker"
      data-testid="checker-section"
      className="relative overflow-hidden bg-[#F8FAFC] py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-60" />
      <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="text-center">
          <p className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">
            <Sparkles className="h-4 w-4" /> Interactieve Tool
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl">
            Accijns Licentie Checker
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-slate-600">
            Beantwoord enkele korte vragen en ontdek welk accijnsstatuut
            vermoedelijk op uw situatie van toepassing is.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.08)]">
            {/* Progress */}
            <div className="flex items-center justify-between border-b border-slate-100 px-7 py-5">
              <span className="text-sm font-bold text-[#0F172A]">
                Stap {stepNum} van {totalSteps}
              </span>
              <div className="h-2 w-40 overflow-hidden rounded-full bg-slate-100 sm:w-64">
                <motion.div
                  className="h-full rounded-full bg-[#F59E0B]"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>

            <div className="p-7 sm:p-10">
              <AnimatePresence mode="wait">
                {/* STEP 1 - PRODUCT */}
                {stage === "product" && (
                  <motion.div
                    key="product"
                    data-testid="checker-step-product"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="font-display text-xl font-bold text-[#0F172A]">
                      1. Welk producttype?
                    </h3>
                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {PRODUCT_OPTIONS.map((opt) => {
                        const Icon = ICONS[opt.icon];
                        const on = product === opt.value;
                        return (
                          <button
                            key={opt.value}
                            data-testid={`product-option-${opt.value}`}
                            onClick={() => pickProduct(opt.value)}
                            className={`flex flex-col items-start gap-4 rounded-2xl border p-5 text-left transition-colors duration-200 ${
                              on
                                ? "border-[#F59E0B] bg-[#F59E0B]/5"
                                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                            }`}
                          >
                            <span
                              className={`grid h-12 w-12 place-items-center rounded-xl ${
                                on
                                  ? "bg-[#F59E0B] text-[#0F172A]"
                                  : "bg-slate-100 text-[#0F172A]"
                              }`}
                            >
                              <Icon className="h-6 w-6" />
                            </span>
                            <span className="font-semibold text-[#0F172A]">
                              {opt.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 - ACTIVITY */}
                {stage === "activity" && (
                  <motion.div
                    key="activity"
                    data-testid="checker-step-activity"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="font-display text-xl font-bold text-[#0F172A]">
                      2. Welke activiteit?
                    </h3>
                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {ACTIVITY_OPTIONS.map((opt) => {
                        const Icon = ICONS[opt.icon];
                        const on = activity === opt.value;
                        return (
                          <button
                            key={opt.value}
                            data-testid={`activity-option-${opt.value}`}
                            onClick={() => pickActivity(opt.value)}
                            className={cardCls(on)}
                          >
                            <span className={iconCls(on)}>
                              <Icon className="h-5 w-5" />
                            </span>
                            <span className="font-semibold text-[#0F172A]">
                              {opt.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      data-testid="checker-back-to-product"
                      onClick={() => setStage("product")}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-[#0F172A]"
                    >
                      <ArrowLeft className="h-4 w-4" /> Terug
                    </button>
                  </motion.div>
                )}

                {/* STEP 3 - REGIME (conditional) */}
                {stage === "regime" && (
                  <motion.div
                    key="regime"
                    data-testid="checker-step-regime"
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="font-display text-xl font-bold text-[#0F172A]">
                      3. Onder welke accijnsregeling?
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">
                      Voor {activityLabel?.toLowerCase()}: zijn de accijnzen reeds
                      betaald of gebeurt dit onder schorsing?
                    </p>
                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {REGIME_OPTIONS.map((opt) => {
                        const Icon = ICONS[opt.icon];
                        const on = regime === opt.value;
                        return (
                          <button
                            key={opt.value}
                            data-testid={`regime-option-${opt.value}`}
                            onClick={() => pickRegime(opt.value)}
                            className={cardCls(on)}
                          >
                            <span className={iconCls(on)}>
                              <Icon className="h-5 w-5" />
                            </span>
                            <span className="font-semibold text-[#0F172A]">
                              {opt.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <button
                      data-testid="checker-back-to-activity"
                      onClick={() => setStage("activity")}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-[#0F172A]"
                    >
                      <ArrowLeft className="h-4 w-4" /> Terug
                    </button>
                  </motion.div>
                )}

                {/* RESULT */}
                {stage === "result" && result && (
                  <motion.div
                    key="result"
                    data-testid="checker-result"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45 }}
                  >
                    <div className="beam-border rounded-3xl p-[1.5px]">
                      <div className="relative z-10 rounded-[calc(1.5rem-1.5px)] bg-[#0F172A] p-8 text-white sm:p-10">
                        <div className="flex items-center gap-2 text-[#F59E0B]">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="text-xs font-bold uppercase tracking-[0.2em]">
                            Aanbevolen statuut
                          </span>
                        </div>
                        <p
                          data-testid="checker-result-status"
                          className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl"
                        >
                          {result.status}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                          <span className="rounded-full bg-white/10 px-3 py-1 text-slate-200">
                            {productLabel}
                          </span>
                          <span className="rounded-full bg-white/10 px-3 py-1 text-slate-200">
                            {activityLabel}
                          </span>
                          {regimeLabel && (
                            <span className="rounded-full bg-white/10 px-3 py-1 text-slate-200">
                              {regimeLabel}
                            </span>
                          )}
                        </div>
                        <p className="mt-5 max-w-lg leading-relaxed text-slate-300">
                          {result.reason}
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                          <button
                            data-testid="checker-request-cta"
                            onClick={() => scrollToId("#contact")}
                            className="group inline-flex items-center gap-2 rounded-full bg-[#F59E0B] px-6 py-3 text-sm font-bold text-[#0F172A] transition-transform duration-200 hover:-translate-y-0.5"
                          >
                            Vraag begeleiding aan
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </button>
                          <button
                            data-testid="checker-reset"
                            onClick={reset}
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
                          >
                            <RotateCcw className="h-4 w-4" /> Opnieuw
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="mt-4 text-center text-xs text-slate-400">
                      Indicatief resultaat — geen bindend fiscaal advies. Onze
                      experts bevestigen uw exacte statuut.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
